import React from "react";
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { useFetchData } from 'services/queries';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
import { useTheme, useMediaQuery, Skeleton } from '@mui/material';

const HeaderBoxes = (props) => {
    const { startDate, endDate, status } = props;
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));

    const notInvoiceIncomingStatuses = [
      'Open',
      'Awaiting Approval',
      'Discontinued',
      'Ready for Processing'
    ];

    const invoiceIncomingStatuses = status
        .filter(status => !notInvoiceIncomingStatuses.includes(status.status))
        .map(status => status.id)
        .join(',');

    const openStatusesOrders = [
        'Open',
      'Awaiting Approval',
      'Ready for Processing',
    ];
    const openIds = status
      .filter(status => openStatusesOrders.includes(status.status))
      .map(status => status.id)
      .join(',');

    const statusDiscontinuedId = status.find(status => status.status === 'Discontinued').id;

    let params_url = ''
    if (startDate || endDate) {
        params_url = `?issue_date__gte=${startDate}&issue_date__lte=${endDate}`;
    }
    const { data: dataOpenOrder, error: errorDataOpenOrder, isLoading: isLoadingDataOpenOrder } = useFetchData(`order/sales-order/total-invoice-value/${params_url}&status__in=${openIds}`, true);
    const { data: dataClosedOrder, error: errorDataClosedOrder, isLoading: isLoadingDataClosedOrder } = useFetchData(`order/sales-order/total-invoice-value/${params_url}&status__in=${invoiceIncomingStatuses}`, true);
    const { data: dataGrossMargin, error: errorDataGrossMargin, isLoading: isLoadingDataGrossMargin } = useFetchData(`order/sales-order/get-gross-margin/${params_url}&status__in=${invoiceIncomingStatuses}`, true);
    const { data: dataAverageTicket, error: errorDataAverageTicket, isLoading: isLoadingDataAverageTicket } = useFetchData(`order/sales-order/get-average-ticket/${params_url}`, true);
    const { data: dataTotalDiscontinued, error: errorDataTotalDiscontinued, isLoading: isLoadingDataTotalDiscontinued } = useFetchData(`order/sales-order/total-invoice-value/${params_url}&status=${statusDiscontinuedId}`, true);

    const isLoading = isLoadingDataOpenOrder || isLoadingDataClosedOrder || isLoadingDataGrossMargin || isLoadingDataAverageTicket || isLoadingDataTotalDiscontinued;
    const hasError = errorDataOpenOrder || errorDataClosedOrder || errorDataGrossMargin || errorDataAverageTicket || errorDataTotalDiscontinued;

    if (isLoading) {
        return (
            <Grid container spacing={1} columns={12}>
                {[1, 2, 3, 4, 5].map((item) => (
                    <Grid xs={6} sm={4} md={2.4} key={item}>
                        <Skeleton variant="rounded" height={70} sx={{ mt: 1 }} />
                    </Grid>
                ))}
            </Grid>
        );
    }

    if (hasError) {
        return <Box p={1}>Error loading dashboard data</Box>;
    }

    // Prepare metrics data for rendering
    const metrics = [
        {
            title: "Opened Orders",
            value: dataOpenOrder.total_value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
            count: dataOpenOrder.count,
            color: 'rgba(58, 133, 245, 0.1)',
            textColor: 'primary.main'
        },
        {
            title: "Total Revenue",
            value: dataClosedOrder.total_value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
            count: dataClosedOrder.count,
            color: 'rgba(34, 197, 94, 0.1)',
            textColor: 'success.main'
        },
        {
            title: "Gross Margin",
            value: dataGrossMargin.gross_margin.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
            count: '$',
            color: 'rgba(34, 197, 94, 0.1)',
            textColor: 'success.main'
        },
        {
            title: "Avg. Ticket / Order",
            value: dataAverageTicket.average_ticket.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
            count: '$',
            color: 'rgba(99, 102, 241, 0.1)',
            textColor: 'info.main'
        },
        {
            title: "Total Discontinued",
            value: dataTotalDiscontinued && dataTotalDiscontinued.total_value
                ? dataTotalDiscontinued.total_value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                : '$0.00',
            count: dataTotalDiscontinued ? dataTotalDiscontinued.count : 0,
            color: 'rgba(234, 67, 53, 0.1)',
            textColor: 'error.main'
        },
    ];

    return (
        <Grid container spacing={1} columns={12} sx={{ mt: 0.5 }}>
            {metrics.map((metric, index) => (
                <Grid xs={6} sm={4} md={2.4} key={index}>
                    <Paper
                        elevation={1}
                        sx={{
                            p: { xs: 1, sm: 1.5 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            height: { xs: '70px', sm: '80px' },
                            borderRadius: 1,
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: 2
                            }
                        }}
                    >
                        <Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    display: 'block',
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                }}
                            >
                                {metric.title}
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: { xs: '110px', sm: '160px', md: '100%' }
                                }}
                            >
                                {metric.value}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                bgcolor: metric.color,
                                color: metric.textColor,
                                borderRadius: '50%',
                                width: { xs: '28px', sm: '32px' },
                                height: { xs: '28px', sm: '32px' },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                fontWeight: 600
                            }}
                        >
                            {metric.count}
                        </Box>
                    </Paper>
                </Grid>
            ))}
        </Grid>
    );
};

export default HeaderBoxes;